import React, { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  TrendingUp, CreditCard, ShoppingCart, ArrowDown, RefreshCw, AlertCircle, CalendarRange,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

function fmtARS(n) {
  return `$ ${Number(n || 0).toLocaleString('es-AR', { maximumFractionDigits: 0 })}`;
}
function pctLabel(v) {
  return (v === null || v === undefined) ? '—' : `${v}%`;
}
function todayIso() {
  const d = new Date();
  const tz = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
  return tz.toISOString().slice(0, 10);
}
function addDaysIso(iso, n) {
  const d = new Date(iso + 'T00:00:00');
  d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
}
async function adminGet(path, { adminSecret, token }) {
  const res = await fetch(`${API}${path}`, {
    headers: { 'X-Admin-Secret': adminSecret, Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

const PRESETS = {
  hoy: () => ({ from_date: todayIso(), to_date: todayIso() }),
  ayer: () => { const y = addDaysIso(todayIso(), -1); return { from_date: y, to_date: y }; },
  '7d': () => ({ from_date: addDaysIso(todayIso(), -6), to_date: todayIso() }),
  '30d': () => ({ from_date: addDaysIso(todayIso(), -29), to_date: todayIso() }),
};

export default function CheckoutAnalyticsSection({ adminSecret, token }) {
  const [source, setSource] = useState('brick');
  const [excludeTests, setExcludeTests] = useState(true);
  const [excludeBots, setExcludeBots] = useState(true);
  const [preset, setPreset] = useState('7d');
  const [fromDate, setFromDate] = useState(PRESETS['7d']().from_date);
  const [toDate, setToDate] = useState(PRESETS['7d']().to_date);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [funnel, setFunnel] = useState(null);
  const [heatmap, setHeatmap] = useState(null);
  const [revenue, setRevenue] = useState(null);
  const [abandoned, setAbandoned] = useState(null);

  const applyPreset = (p) => {
    setPreset(p);
    if (PRESETS[p]) {
      const { from_date, to_date } = PRESETS[p]();
      setFromDate(from_date);
      setToDate(to_date);
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true); setError(null);
      try {
        const q = `from_date=${fromDate}&to_date=${toDate}&source=${source}&exclude_tests=${excludeTests}&exclude_bots=${excludeBots}`;
        const [f, h, r, a] = await Promise.all([
          adminGet(`/api/admin/dashboard/brick-funnel?${q}`, { adminSecret, token }),
          adminGet(`/api/admin/dashboard/checkout-heatmap?${q}`, { adminSecret, token }),
          adminGet(`/api/admin/dashboard/revenue?days=30&exclude_tests=${excludeTests}`, { adminSecret, token }),
          adminGet(`/api/admin/dashboard/abandoned-carts?days=30&limit=200`, { adminSecret, token }),
        ]);
        if (cancelled) return;
        setFunnel(f); setHeatmap(h); setRevenue(r); setAbandoned(a);
      } catch (e) {
        if (!cancelled) setError(e.message || 'Error cargando analytics');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [adminSecret, token, source, excludeTests, excludeBots, fromDate, toDate]);

  return (
    <section className="mb-8" data-testid="checkout-analytics">
      <div className="flex items-end justify-between mb-3 flex-wrap gap-3">
        <h2 className="text-xs uppercase tracking-wider text-white font-semibold">Analítica de checkout</h2>
        <div className="flex items-center gap-2 flex-wrap">
          <select
            value={source}
            onChange={(e) => setSource(e.target.value)}
            data-testid="funnel-source-select"
            className="bg-white/5 border border-white/10 text-white text-xs rounded-lg px-2.5 py-1.5 focus:outline-none focus:ring-1 focus:ring-[#5B9B2F]"
          >
            <option value="brick">Source: Brick</option>
            <option value="checkout_pro">Source: Checkout Pro</option>
            <option value="all">Source: Todos</option>
          </select>
          <label className="flex items-center gap-1.5 text-xs text-slate-300">
            <input type="checkbox" checked={excludeTests} onChange={(e) => setExcludeTests(e.target.checked)} data-testid="funnel-exclude-tests" className="accent-[#5B9B2F]" />
            Excluir tests
          </label>
          <label className="flex items-center gap-1.5 text-xs text-slate-300">
            <input type="checkbox" checked={excludeBots} onChange={(e) => setExcludeBots(e.target.checked)} data-testid="funnel-exclude-bots" className="accent-[#5B9B2F]" />
            Excluir bots
          </label>
        </div>
      </div>

      {/* Selector de rango */}
      <div className="bg-white/5 border border-white/10 rounded-2xl p-3 mb-3 flex items-center gap-3 flex-wrap" data-testid="range-selector">
        <CalendarRange className="h-4 w-4 text-slate-400" />
        <div className="flex items-center gap-1">
          {[['hoy','Hoy'], ['ayer','Ayer'], ['7d','Últimos 7 días'], ['30d','Últimos 30 días']].map(([k,label]) => (
            <Button
              key={k}
              size="sm"
              variant={preset === k ? 'default' : 'outline'}
              onClick={() => applyPreset(k)}
              data-testid={`range-preset-${k}`}
              className={preset === k
                ? 'bg-[#5B9B2F] hover:bg-[#4a8126] text-white h-7 text-xs'
                : 'bg-white/5 border-white/10 text-slate-300 hover:bg-white/10 h-7 text-xs'}
            >
              {label}
            </Button>
          ))}
        </div>
        <div className="flex items-center gap-2 ml-auto">
          <Input
            type="date"
            value={fromDate}
            onChange={(e) => { setFromDate(e.target.value); setPreset('custom'); }}
            data-testid="range-from"
            className="bg-white/5 border-white/10 text-white text-xs h-7 w-36"
          />
          <span className="text-slate-500 text-xs">→</span>
          <Input
            type="date"
            value={toDate}
            onChange={(e) => { setToDate(e.target.value); setPreset('custom'); }}
            data-testid="range-to"
            className="bg-white/5 border-white/10 text-white text-xs h-7 w-36"
          />
          {loading && <RefreshCw className="h-3.5 w-3.5 animate-spin text-slate-500" />}
        </div>
      </div>

      {error && (
        <div className="bg-[#C83A2A]/10 border border-[#C83A2A]/30 rounded-xl p-3 text-[#C83A2A] text-sm flex items-center gap-2 mb-3">
          <AlertCircle className="h-4 w-4" /> {error}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Funnel */}
        <div className="bg-white/5 border border-white/10 rounded-2xl p-5" data-testid="funnel-card">
          <div className="flex items-center justify-between mb-4">
            <p className="text-white font-semibold text-sm">Embudo {source === 'brick' ? 'Bricks' : source === 'checkout_pro' ? 'Checkout Pro' : '(todos)'}</p>
            <span className="text-[10px] uppercase tracking-wider text-slate-500">{fromDate} → {toDate}</span>
          </div>

          {/* Métrica clave punta-a-punta: Cargó → Pagó */}
          {funnel?.end_to_end && (
            <div
              className="mb-5 bg-gradient-to-br from-[#5B9B2F]/20 to-[#5B9B2F]/5 border border-[#5B9B2F]/40 rounded-xl p-4"
              data-testid="funnel-end-to-end"
              title={funnel.end_to_end.description}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-[10px] uppercase tracking-wider text-[#5B9B2F] font-bold mb-1">
                    Conversión total del checkout
                  </p>
                  <p className="text-slate-300 text-xs leading-snug">
                    {funnel.end_to_end.label}
                  </p>
                </div>
                <div className="text-right flex-shrink-0">
                  <p
                    className="text-3xl sm:text-4xl font-bold text-white tabular-nums leading-none"
                    data-testid="funnel-end-to-end-pct"
                  >
                    {funnel.end_to_end.conversion_pct}%
                  </p>
                  <p className="text-[10px] text-slate-400 mt-1 tabular-nums">
                    {funnel.end_to_end.paid} / {funnel.end_to_end.loaded}
                  </p>
                </div>
              </div>
            </div>
          )}

          <div className="space-y-2">
            {funnel?.funnel?.map((step, idx) => {
              const stepIcons = [TrendingUp, CreditCard, ShoppingCart];
              const Icon = stepIcons[Math.min(idx, stepIcons.length - 1)];
              const widthPct = step.pct_of_top || 0;
              return (
                <div key={step.step} data-testid={`funnel-step-${step.step}`}>
                  <div className="flex items-center justify-between text-xs mb-1">
                    <span className="text-slate-300 flex items-center gap-1.5">
                      <Icon className="h-3.5 w-3.5 text-[#5B9B2F]" />
                      {step.label}
                    </span>
                    <span className="font-mono text-white tabular-nums">
                      {step.count} <span className="text-slate-500">· {step.pct_of_top}%</span>
                    </span>
                  </div>
                  <div className="h-2 bg-white/5 rounded-full overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-[#5B9B2F] to-[#7ec84a] transition-all" style={{ width: `${widthPct}%` }} />
                  </div>
                  {step.drop_from_prev_pct !== null && step.drop_from_prev_pct !== undefined && step.step > 1 && (
                    <p className="text-[10px] text-[#C83A2A] mt-0.5 flex items-center gap-0.5">
                      <ArrowDown className="h-2.5 w-2.5" />Caída {pctLabel(step.drop_from_prev_pct)} vs paso anterior
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Ingreso bruto vs neto */}
        <div className="bg-white/5 border border-white/10 rounded-2xl p-5" data-testid="revenue-card">
          <p className="text-white font-semibold text-sm mb-4">Ingreso (últimos 30d)</p>
          {revenue && (
            <div className="space-y-3">
              <div className="flex items-baseline justify-between">
                <span className="text-slate-400 text-xs">Bruto</span>
                <span className="text-white text-xl font-bold tabular-nums">{fmtARS(revenue.gross_ars)}</span>
              </div>
              <div className="flex items-baseline justify-between">
                <span className="text-[#C83A2A] text-xs">− Comisión MP</span>
                <span className="text-[#C83A2A] text-sm tabular-nums">−{fmtARS(revenue.fee_ars)}</span>
              </div>
              <div className="border-t border-white/10 my-2" />
              <div className="flex items-baseline justify-between">
                <span className="text-[#5B9B2F] text-xs font-semibold">Neto estimado</span>
                <span className="text-[#5B9B2F] text-2xl font-bold tabular-nums" data-testid="revenue-net">{fmtARS(revenue.net_ars)}</span>
              </div>
              <p className="text-[10px] text-slate-500 mt-2 leading-relaxed">
                {revenue.count_total} pagos aprobados ({revenue.count_with_installments} con cuotas).
                Tasas: {(revenue.rates_used.default * 100).toFixed(2)}% acreditación inmediata ·
                {' '}{(revenue.rates_used.with_installments * 100).toFixed(2)}% con cuotas s/interés.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Mapa de calor: día de la semana × hora */}
      <div className="bg-white/5 border border-white/10 rounded-2xl p-5 mt-4" data-testid="heatmap-card">
        <MapaDeCalor datos={heatmap} desde={fromDate} hasta={toDate} />
      </div>

      {/* Carritos abandonados */}
      <div className="bg-white/5 border border-white/10 rounded-2xl mt-4" data-testid="abandoned-card">
        <div className="px-5 py-3 border-b border-white/10 flex items-center justify-between">
          <p className="text-white font-semibold text-sm">
            Carritos abandonados con email <span className="text-slate-500 font-normal">(30d, ordenado por más reciente)</span>
          </p>
          <span className="text-xs text-slate-400 tabular-nums">{abandoned?.count || 0} sesiones</span>
        </div>
        <div className="overflow-x-auto max-h-80 overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="bg-white/5 text-slate-400 text-xs uppercase tracking-wider sticky top-0">
              <tr>
                <th className="px-4 py-2 text-left">Email</th>
                <th className="px-4 py-2 text-left">Step 1 (email)</th>
                <th className="px-4 py-2 text-left">Inicio sesión</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {(abandoned?.items || []).map((row) => (
                <tr key={row.external_reference} className="text-slate-300 hover:bg-white/5">
                  <td className="px-4 py-2 font-mono text-xs">{row.email}</td>
                  <td className="px-4 py-2 text-xs text-slate-400">{(row.step_email_at || '').slice(0,19).replace('T',' ')}</td>
                  <td className="px-4 py-2 text-xs text-slate-500">{(row.created_at || '').slice(0,19).replace('T',' ')}</td>
                </tr>
              ))}
              {(!abandoned?.items || abandoned.items.length === 0) && (
                <tr><td colSpan={3} className="px-4 py-6 text-center text-slate-500 text-xs">Sin carritos abandonados en este rango.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}

const VISTAS_MAPA = [
  ['cargo', 'Cargaron el checkout', '91,155,47'],
  ['pago', 'Pagaron', '123,194,74'],
];

/** Cuándo cargan el checkout y cuándo pagan: día × hora argentina. */
function MapaDeCalor({ datos, desde, hasta }) {
  const [vista, setVista] = useState('cargo');
  const [, nombre, rgb] = VISTAS_MAPA.find(([k]) => k === vista);
  const grilla = datos?.[vista] || [];
  const total = datos ? datos[`total_${vista}`] : 0;
  const max = Math.max(1, ...grilla.flat());
  const horas = Array.from({ length: 24 }, (_, h) => h);

  return (
    <div>
      <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
        <div>
          <p className="text-white font-semibold text-sm">¿Cuándo compran?</p>
          <p className="text-[11px] text-slate-500">
            Día y hora (Argentina) · {desde} → {hasta} · {total} en total
          </p>
        </div>
        <div className="flex rounded-lg border border-white/10 overflow-hidden" data-testid="heatmap-vista">
          {VISTAS_MAPA.map(([k, label]) => (
            <button
              key={k}
              type="button"
              onClick={() => setVista(k)}
              className={`px-2.5 py-1 text-xs font-medium transition ${
                vista === k ? 'bg-[#5B9B2F] text-white' : 'text-slate-300 hover:bg-white/5'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {!datos ? <p className="text-slate-500 text-xs">Cargando…</p> : (
        <div className="overflow-x-auto">
          <table className="border-separate" style={{ borderSpacing: 2 }} data-testid="heatmap">
            <thead>
              <tr>
                <th />
                {horas.map((h) => (
                  <th key={h} className="text-[9px] font-normal text-slate-500 w-6 text-center tabular-nums">
                    {h % 3 === 0 ? h : ''}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {datos.dias.map((dia, i) => (
                <tr key={dia}>
                  <td className="text-[10px] text-slate-400 pr-2 text-right">{dia}</td>
                  {horas.map((h) => {
                    const n = grilla[i][h];
                    const alfa = n ? 0.2 + 0.8 * (n / max) : 0;
                    return (
                      <td
                        key={h}
                        title={`${dia} ${h}:00–${h}:59 · ${n} ${nombre.toLowerCase()}`}
                        className="w-6 h-6 rounded-sm text-center text-[9px] tabular-nums text-white/90"
                        style={{ background: n ? `rgba(${rgb},${alfa})` : 'rgba(255,255,255,0.04)' }}
                      >
                        {n || ''}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {datos && total === 0 && (
        <p className="text-slate-500 text-xs mt-2">Sin datos en el rango.</p>
      )}
    </div>
  );
}
